import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BtnSimple } from '../form/btn.Simple';
import {
  FaArrowDown,
  FaArrowUp,
  FaPlus,
  FaTrashAlt,
  FaUndo,
  FaPalette,
  FaImage,
  FaTextHeight,
} from 'react-icons/fa';
import { SearchInput } from '../search/search.Input';
import { getId, getItemPicAsset } from '../../utils/item';
import useItems from '../../hooks/useItems';
import { ColorsSwatch } from '../form/colorsSwatch';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { PicSelectionOptions } from '../pic/pic.Selector';
import { buildTimelineEntryLabel } from '../../utils/timeline';
import {
  WidgetSettingsRoot,
  WidgetSettingsCollapsibleSection,
  SettingsRow,
  SettingsInput,
  DangerZone,
} from '../form/settings.Widget';

export const TimelineSettings = props => {
  const {
    itemId,
    timelineId,
    timeline,
    onTimelineChange,
    onEntriesChange,
    onDeleteTimeline,
  } = props;

  const items = useSelector(state => state.items);
  const [selectedItemId, setSelectedItemId] = useState(null);

  useItems(selectedItemId ? [selectedItemId] : []);
  useItems(timeline?.entries?.map(entry => entry.itemId) || []);

  const selectedItem = selectedItemId ? items[selectedItemId] : null;
  const candidateNums = selectedItem?.has_num || [];

  const entriesWithMeta = useMemo(() => {
    return (timeline?.entries || []).map(entry => {
      const entryItem = items[entry.itemId];
      const numObj = entryItem?.has_num?.find(
        num => getId(num) === entry.numId,
      );
      const numPayload = numObj ? numObj[entry.numId] : undefined;
      const defaultLabel = buildTimelineEntryLabel(
        entryItem?.label,
        numPayload?.label,
        { emptyFallback: entry.label ?? 'Entrée' },
      );
      const storedLabel = typeof entry.label === 'string' ? entry.label : '';
      const normalizedStored = storedLabel.trim();
      const hasCustomLabel =
        entry.labelLocked === true ||
        (entry.labelLocked !== true &&
          normalizedStored.length > 0 &&
          normalizedStored !== defaultLabel);
      const effectiveLabel = hasCustomLabel
        ? storedLabel || defaultLabel
        : defaultLabel;
      const picOptions = (entryItem?.pics || []).map((picObj, picIndex) => {
        const picId = getId(picObj);
        const picPayload = picObj?.[picId] || {};
        return {
          id: picId,
          url: picPayload?.url,
          iconSlug: picPayload?.iconSlug,
          label: picPayload?.label || `Image ${picIndex + 1}`,
        };
      });
      return {
        ...entry,
        itemLabel: entryItem?.label,
        numLabel: numPayload?.label,
        defaultLabel,
        effectiveLabel,
        hasCustomLabel,
        picOptions,
      };
    });
  }, [timeline?.entries, items]);

  const handleTimelineLabel = value => {
    onTimelineChange({ label: value });
  };

  const handleOrderToggle = () => {
    const nextOrder = timeline?.order === 'desc' ? 'asc' : 'desc';
    onTimelineChange({ order: nextOrder });
  };

  const handleResetSelection = () => {
    setSelectedItemId(null);
  };

  const handleAddEntry = numObj => {
    if (!selectedItemId) {
      return;
    }
    const numId = getId(numObj);
    const existingEntries = timeline?.entries || [];
    const alreadyExist = existingEntries.some(
      entry => entry.itemId === selectedItemId && entry.numId === numId,
    );
    if (alreadyExist) {
      return;
    }
    const numData = numObj[numId];
    const entryItem = items[selectedItemId];
    const avatarAsset = getItemPicAsset(entryItem);
    const defaultColor =
      avatarAsset?.bgColor && avatarAsset.bgColor !== 'transparent'
        ? avatarAsset.bgColor
        : undefined;
    const defaultLabel = buildTimelineEntryLabel(
      entryItem?.label,
      numData?.label,
      {
        fallbackItemLabel: 'Item',
        fallbackNumLabel: 'Num',
      },
    );
    const newEntry = {
      itemId: selectedItemId,
      numId,
      label: defaultLabel,
      labelLocked: false,
      color: defaultColor,
    };

    onEntriesChange([...existingEntries, newEntry]);
  };

  const handleRemoveEntry = index => {
    const existingEntries = timeline?.entries || [];
    const newEntries = existingEntries.filter((_, i) => i !== index);
    onEntriesChange(newEntries);
  };

  const handleEntryLabelChange = (index, value) => {
    const existingEntries = timeline?.entries || [];
    const entryMeta = entriesWithMeta?.[index];
    const defaultLabel = entryMeta?.defaultLabel ?? '';
    const normalized = typeof value === 'string' ? value.trim() : '';
    const useDefault = normalized === '' || normalized === defaultLabel;
    const newEntries = existingEntries.map((entry, i) => {
      if (i !== index) {
        return entry;
      }
      const updatedEntry = { ...entry };
      if (useDefault) {
        updatedEntry.label = defaultLabel;
        updatedEntry.labelLocked = false;
      } else {
        updatedEntry.label = normalized;
        updatedEntry.labelLocked = true;
      }
      return updatedEntry;
    });
    onEntriesChange(newEntries);
  };

  const handleEntryPicChange = (index, newValue) => {
    const parsedValue =
      newValue === 'default'
        ? undefined
        : newValue === 'none'
          ? null
          : newValue;
    const newEntries = (timeline?.entries || []).map((entry, i) =>
      i === index ? { ...entry, picId: parsedValue } : entry,
    );
    onEntriesChange(newEntries);
  };

  const handleEntrySizeChange = (index, size) => {
    const newEntries = (timeline?.entries || []).map((entry, i) =>
      i === index ? { ...entry, size: size || undefined } : entry,
    );
    onEntriesChange(newEntries);
  };

  const parseNumberOrExpression = value => {
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') {
      return undefined;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    // Allow dynamic values like `[current.numId]` or `[itemId.numId]`.
    return trimmed;
  };

  const handleMinChange = value => {
    onTimelineChange({ min: parseNumberOrExpression(value) });
  };

  const handleMaxChange = value => {
    onTimelineChange({ max: parseNumberOrExpression(value) });
  };

  const toggleLabels = () => {
    const nextValue = timeline?.showLabels === false ? true : false;
    onTimelineChange({ showLabels: nextValue });
  };

  const toggleTicks = () => {
    const nextValue = timeline?.showTicks === false ? true : false;
    onTimelineChange({ showTicks: nextValue });
  };

  const toggleTooltips = () => {
    const nextValue = timeline?.showPointTooltips === false ? true : false;
    onTimelineChange({ showPointTooltips: nextValue });
  };

  const toggleDrawerOpen = () => {
    const nextValue = timeline?.openDrawerOnClick === false ? true : false;
    onTimelineChange({ openDrawerOnClick: nextValue });
  };

  const toggleDraggablePoints = () => {
    const nextValue = timeline?.pointsDraggable === false ? true : false;
    onTimelineChange({ pointsDraggable: nextValue });
  };

  const toggleTickLabelOrientation = () => {
    const current = timeline?.tickLabelsVertical === true;
    onTimelineChange({ tickLabelsVertical: current ? false : true });
  };

  const handleTickStepChange = value => {
    onTimelineChange({ tickStep: parseNumberOrExpression(value) });
  };

  const toggleCards = [
    {
      key: 'labels',
      label: 'Labels des points',
      help: 'Affiche le nom ou le libellé de chaque point sur la timeline.',
      value: timeline?.showLabels !== false,
      onToggle: toggleLabels,
      buttonText: value => (value ? 'Affichés' : 'Masqués'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'values',
      label: 'Valeur sous les points',
      help: 'Montre la valeur numérique directement sous chaque point.',
      value: timeline?.showValues !== false,
      onToggle: () =>
        onTimelineChange({
          showValues: timeline?.showValues === false ? true : false,
        }),
      buttonText: value => (value ? 'Affichées' : 'Masquées'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'zoom',
      label: 'Contrôle de zoom',
      help: 'Affiche le slider permettant de zoomer sur la timeline.',
      value: timeline?.showZoom !== false,
      onToggle: () =>
        onTimelineChange({
          showZoom: timeline?.showZoom === false ? true : false,
        }),
      buttonText: value => (value ? 'Affiché' : 'Masqué'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'ticks',
      label: 'Graduations automatiques',
      help: 'Affiche les graduations numériques sur l’axe principal.',
      value: timeline?.showTicks !== false,
      onToggle: toggleTicks,
      buttonText: value => (value ? 'Affichées' : 'Masquées'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'orientation',
      label: 'Orientation des graduations',
      help: 'Passe les étiquettes de graduations en mode vertical ou horizontal.',
      value: timeline?.tickLabelsVertical === true,
      onToggle: toggleTickLabelOrientation,
      buttonText: value => (value ? 'Vertical' : 'Horizontal'),
      buttonClass: value => (value ? 'bg-primary' : 'bg-secondary'),
    },
    {
      key: 'tooltips',
      label: 'Info-bulles des points',
      help: 'Affiche une info-bulle contenant toutes les entrées regroupées.',
      value: timeline?.showPointTooltips !== false,
      onToggle: toggleTooltips,
      buttonText: value => (value ? 'Affichées' : 'Masquées'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'drawer',
      label: 'Ouverture du drawer',
      help: 'Ouvre la fiche de l’item associé lorsque le point est cliqué.',
      value: timeline?.openDrawerOnClick !== false,
      onToggle: toggleDrawerOpen,
      buttonText: value => (value ? 'Activé' : 'Désactivé'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
    {
      key: 'drag',
      label: 'Déplacement des points',
      help: 'Autorise le drag & drop pour modifier la valeur des nums.',
      value: timeline?.pointsDraggable !== false,
      onToggle: toggleDraggablePoints,
      buttonText: value => (value ? 'Activé' : 'Désactivé'),
      buttonClass: value => (value ? 'bg-secondary' : 'bg-primary'),
    },
  ];

  const palette = [
    'secondary',
    'secondary-d',
    'secondary-l',
    'primary',
    'primary-l',
    'primary-d',
    'contrast',
    'contrast-l',
    'red',
    'orange',
    'yellow',
    'lime',
    'green',
    'blue',
    'purple',
    'pink',
  ];

  const handleColorChange = (index, colorKey) => {
    const entries = (timeline?.entries || []).map((entry, i) =>
      i === index ? { ...entry, color: colorKey } : entry,
    );
    onEntriesChange(entries);
  };

  const getEntryPicSelectValue = entry => {
    if (entry?.picId === null) {
      return 'none';
    }
    if (typeof entry?.picId === 'string') {
      return entry.picId;
    }
    return 'default';
  };

  const getTickLabels = () =>
    Array.isArray(timeline?.tickLabels) ? timeline.tickLabels : [];

  const getSortedTickLabels = () => {
    const entries = getTickLabels();
    return entries
      .map((entry, originalIndex) => ({ entry, originalIndex }))
      .sort((a, b) => {
        const aValue =
          typeof a.entry?.value === 'number' && Number.isFinite(a.entry.value)
            ? a.entry.value
            : Number.POSITIVE_INFINITY;
        const bValue =
          typeof b.entry?.value === 'number' && Number.isFinite(b.entry.value)
            ? b.entry.value
            : Number.POSITIVE_INFINITY;
        if (aValue !== bValue) {
          return aValue - bValue;
        }
        // Keep a stable order for identical/empty values.
        return a.originalIndex - b.originalIndex;
      });
  };

  const updateTickLabels = nextLabels => {
    onTimelineChange({ tickLabels: nextLabels });
  };

  const handleAddTickLabel = () => {
    const existing = getTickLabels();
    updateTickLabels([
      ...existing,
      {
        value:
          existing.length > 0 ? (existing[existing.length - 1]?.value ?? 0) : 0,
        label: '',
        labelTop: '',
        labelBottom: '',
      },
    ]);
  };

  const handleTickLabelFieldChange = (index, field, rawValue) => {
    updateTickLabels(
      getTickLabels().map((entry, i) => {
        if (i !== index) {
          return entry;
        }
        if (field === 'value') {
          if (rawValue === '') {
            return { ...entry, value: undefined };
          }
          const parsed = Number(rawValue);
          if (!Number.isFinite(parsed)) {
            return entry;
          }
          return { ...entry, value: parsed };
        }
        if (field === 'labelBottom') {
          return { ...entry, labelBottom: rawValue, label: rawValue };
        }
        return { ...entry, [field]: rawValue };
      }),
    );
  };

  const handleRemoveTickLabel = index => {
    updateTickLabels(getTickLabels().filter((_, i) => i !== index));
  };

  return (
    <WidgetSettingsRoot>
      <WidgetSettingsCollapsibleSection title='Général' defaultOpen={true}>
        <div className='flex flex-col gap-05'>
          <div className='flex items-center gap-1'>
            <SettingsInput
              initValue={timeline?.label ?? 'Timeline'}
              placeholder='Nom de la timeline'
              onBlur={handleTimelineLabel}
            />
            <BtnSimple
              className='flex items-center gap-05 whitespace-nowrap'
              onClick={handleOrderToggle}
            >
              {timeline?.order === 'desc' ? (
                <>
                  <FaArrowDown /> décroissant
                </>
              ) : (
                <>
                  <FaArrowUp /> croissant
                </>
              )}
            </BtnSimple>
          </div>

          <div className='flex flex-col gap-05'>
            <SettingsRow label='minimum' dense labelClassName='uppercase text-xs text-opacity-60'>
              <SettingsInput
                initValue={
                  timeline?.min !== undefined && timeline?.min !== null
                    ? String(timeline?.min)
                    : ''
                }
                className='w-full font-mono text-sm'
                placeholder='ex: 0 ou [current.numId]'
                type='text'
                onBlur={handleMinChange}
              />
            </SettingsRow>

            <SettingsRow label='maximum' dense labelClassName='uppercase text-xs text-opacity-60'>
              <SettingsInput
                initValue={
                  timeline?.max !== undefined && timeline?.max !== null
                    ? String(timeline?.max)
                    : ''
                }
                className='w-full font-mono text-sm'
                placeholder='ex: 10 ou [itemId.numId]'
                type='text'
                onBlur={handleMaxChange}
              />
            </SettingsRow>

            <SettingsRow label='pas' dense labelClassName='uppercase text-xs text-opacity-60'>
              <SettingsInput
                initValue={
                  timeline?.tickStep !== undefined &&
                  timeline?.tickStep !== null
                    ? String(timeline?.tickStep)
                    : ''
                }
                className='w-full font-mono text-sm'
                placeholder='ex: 1 ou [current.numId]'
                type='text'
                onBlur={handleTickStepChange}
              />
            </SettingsRow>
          </div>
        </div>
      </WidgetSettingsCollapsibleSection>

      <WidgetSettingsCollapsibleSection
        title="Options d'affichage"
        defaultOpen={false}
      >
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-05'>
          {toggleCards.map(option => (
            <div
              key={option.key}
              className='rounded border border-primary border-opacity-20 p-05 flex flex-col gap-03 bg-main bg-opacity-30'
            >
              <div className='text-xs uppercase text-contrast text-opacity-60'>
                {option.label}
              </div>
              <div className='text-2xs text-contrast text-opacity-60 flex-1'>
                {option.help}
              </div>
              <BtnSimple
                className={`text-xs ${option.buttonClass(option.value)} mt-02`}
                onClick={option.onToggle}
              >
                {option.buttonText(option.value)}
              </BtnSimple>
            </div>
          ))}
        </div>
      </WidgetSettingsCollapsibleSection>

      <WidgetSettingsCollapsibleSection title='Options avancées' defaultOpen={false}>
        <div className='flex flex-col gap-05'>
          <div className='text-xs uppercase text-contrast text-opacity-60 mb-02'>
            Graduations personnalisées
          </div>
          <div className='text-2xs text-contrast text-opacity-60 mb-05'>
            Remplace certaines valeurs de graduation par du texte (ex: 1 → Très
            faible) et, si besoin, définissez un texte distinct
            au-dessus/dessous (droite/gauche en mode vertical).
          </div>
          <BtnSimple
            className='text-xs mb-05'
            onClick={handleAddTickLabel}
          >
            <FaPlus /> Ajouter une graduation
          </BtnSimple>
          {getTickLabels().length === 0 && (
            <div className='text-2xs text-contrast text-opacity-40'>
              Aucun remplacement configuré pour le moment.
            </div>
          )}
          <div className='flex flex-col gap-03'>
            {getSortedTickLabels().map(({ entry, originalIndex }) => (
              <div
                key={`tick-${originalIndex}`}
                className='flex flex-col gap-02 rounded border border-primary border-opacity-10 p-05'
              >
                <div className='flex gap-05 items-center'>
                  <SettingsInput
                    className='w-24'
                    placeholder='Valeur'
                    type='number'
                    initValue={
                      typeof entry?.value === 'number' ? String(entry.value) : ''
                    }
                    onBlur={value =>
                      handleTickLabelFieldChange(originalIndex, 'value', value)
                    }
                  />
                  <div
                    className='cursor-pointer text-secondary text-opacity-70 hover:text-opacity-100 transition'
                    onClick={() => handleRemoveTickLabel(originalIndex)}
                  >
                    <FaTrashAlt />
                  </div>
                </div>
                <div className='flex flex-col gap-02 sm:flex-row'>
                  <SettingsInput
                    className='flex-1'
                    placeholder='Texte au-dessus (ou à droite en vertical)'
                    initValue={entry?.labelTop ?? ''}
                    onBlur={value =>
                      handleTickLabelFieldChange(originalIndex, 'labelTop', value)
                    }
                  />
                  <SettingsInput
                    className='flex-1'
                    placeholder='Texte en dessous (ou à gauche en vertical)'
                    initValue={entry?.labelBottom ?? entry?.label ?? ''}
                    onBlur={value =>
                      handleTickLabelFieldChange(
                        originalIndex,
                        'labelBottom',
                        value
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>

        </div>
      </WidgetSettingsCollapsibleSection>

      <WidgetSettingsCollapsibleSection
        title='Éléments de la timeline'
        defaultOpen={false}
      >
        <div className='flex flex-col gap-05 w-full'>
          <div className='text-xs uppercase text-contrast text-opacity-60 mb-05'>
            Ajouter un item avec un nombre
          </div>
          <SearchInput
            itemId={itemId}
            className='m-0'
            searchIn={{
              type: undefined,
              universe: true,
              world: true,
              campaign: true,
            }}
            callback={foundItem => setSelectedItemId(foundItem?._id ?? null)}
          />
          {selectedItem && (
            <div className='flex items-center gap-1 mt-05'>
              <div className='text-sm text-contrast text-opacity-70 flex-1'>
                {selectedItem.label}
              </div>
              <BtnSimple
                className='flex items-center gap-05 text-xs'
                onClick={handleResetSelection}
              >
                <FaUndo /> changer
              </BtnSimple>
            </div>
          )}
          <div className='mt-05 flex flex-wrap gap-05'>
            {candidateNums.length === 0 && selectedItem && (
              <div className='text-xs text-contrast text-opacity-50'>
                Aucun nombre disponible pour cet item.
              </div>
            )}
            {candidateNums.map(numObj => {
              const numId = getId(numObj);
              const numData = numObj[numId];
              return (
                <BtnSimple
                  key={numId}
                  className='flex items-center gap-05 text-xs'
                  onClick={() => handleAddEntry(numObj)}
                >
                  <FaPlus /> {numData?.label}
                </BtnSimple>
              );
            })}
          </div>

          <div className='border-t border-primary border-opacity-20 pt-05 mt-05' />

          {(entriesWithMeta?.length ?? 0) === 0 && (
            <div className='text-xs text-contrast text-opacity-50'>
              Ajoutez des items pour construire la timeline.
            </div>
          )}
          <div className='flex flex-col gap-05'>
            {entriesWithMeta.map((entry, index) => {
              const picSelectionOptions = [
                { key: 'default', label: 'Avatar par défaut' },
                { key: 'none', label: 'Aucune image' },
                ...(entry.picOptions || []).map(option => ({
                  key: option.id,
                  label: option.label || 'Image',
                  previewUrl: option.url ?? null,
                  iconSlug: option.iconSlug,
                })),
              ];
              const currentPicValue = getEntryPicSelectValue(entry);
              const sizeConfig = { small: 16, medium: 24, large: 32 };
              const pointDiameter =
                sizeConfig[entry?.size || 'medium'] ?? sizeConfig.medium;
              return (
                <div
                  key={`${entry.itemId}-${entry.numId}`}
                  className={`rounded bg-${entry.color} bg-opacity-30 p-05 flex flex-col gap-05`}
                >
                  <div className='flex justify-between items-center text-xs text-contrast text-opacity-60'>
                    <div className='flex gap-02 flex-1'>
                      <span>{entry.itemLabel ?? 'Item'}</span> -
                      <span className='text-contrast text-opacity-50'>
                        {entry.numLabel ?? 'Nombre'}
                      </span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <TooltipHtml
                        placement='left'
                        title={
                          <ColorsSwatch
                            currentColor={entry.color}
                            colors={palette}
                            onClick={color => handleColorChange(index, color)}
                          />
                        }
                      >
                        <div className='p-03 rounded-full bg-primary bg-opacity-40 cursor-pointer hover:bg-opacity-70 transition'>
                          <FaPalette />
                        </div>
                      </TooltipHtml>
                      <TooltipHtml
                        placement='left'
                        title={
                          <PicSelectionOptions
                            options={picSelectionOptions}
                            value={currentPicValue}
                            size={pointDiameter}
                            onChange={value =>
                              handleEntryPicChange(index, value)
                            }
                          />
                        }
                      >
                        <div className='p-03 rounded-full bg-primary bg-opacity-40 cursor-pointer hover:bg-opacity-70 transition'>
                          <FaImage />
                        </div>
                      </TooltipHtml>
                      <TooltipHtml
                        placement='left'
                        title={
                          <div className='flex flex-col gap-02'>
                            <div className='text-3xs uppercase text-contrast text-opacity-60 tracking-wide'>
                              Taille du point
                            </div>
                            <div className='flex gap-02 flex-wrap'>
                              {[
                                { key: 'small', label: 'Petit' },
                                { key: 'medium', label: 'Moyen' },
                                { key: 'large', label: 'Grand' },
                              ].map(option => {
                                const isActiveSize =
                                  (entry?.size || 'medium') === option.key;
                                return (
                                  <div
                                    key={option.key}
                                    className={`cursor-pointer text-xs font-semibold px-03 py-01 rounded transition ${
                                      isActiveSize
                                        ? 'bg-primary text-main'
                                        : 'bg-main text-contrast text-opacity-70'
                                    }`}
                                    onClick={() =>
                                      handleEntrySizeChange(index, option.key)
                                    }
                                  >
                                    {option.label}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        }
                      >
                        <div className='p-03 rounded-full bg-primary bg-opacity-40 cursor-pointer hover:bg-opacity-70 transition'>
                          <FaTextHeight />
                        </div>
                      </TooltipHtml>
                      <div
                        className='cursor-pointer transition hover:opacity-60'
                        onClick={() => handleRemoveEntry(index)}
                      >
                        <FaTrashAlt />
                      </div>
                    </div>
                  </div>

                  <SettingsInput
                    initValue={entry.effectiveLabel}
                    placeholder='Libellé affiché'
                    onBlur={value => handleEntryLabelChange(index, value)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </WidgetSettingsCollapsibleSection>
      <DangerZone
        confirmId={`deleteTimeline-${timelineId}`}
        closeModalId={timelineId}
        message='Voulez-vous vraiment supprimer cette timeline ?'
        onConfirm={() => onDeleteTimeline?.()}
      />
    </WidgetSettingsRoot>
  );
};
